import { FC, memo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import Loader from '@app/components/ui/common/Loader'
import { announce } from '@app/components/common/ScreenReaderAnnouncer'
import { RubricBlockModal } from '@app/components/ui/modals/RubricBlockModal'
import { StartTestModal } from '@app/components/ui/modals/StartTestModal'
import { UserAssessment } from '@app/firebase/api'
import {
  getTerm,
  useAnsweredItems,
  useRubricModal,
  useTestCallback,
  useUserAssessentPauseCallback,
  useUserAssessmentStart,
} from '@app/helpers'
import { TEST_ATTRIBUTE_ID, TEST_ELEMENT_IDS } from '@app/constants'
import { TestPart, View } from '@app/models'
import { CurrentTestState, CurrentUserAssessmentState, HeaderTitleState } from '@app/storage'
import TestPartComponent from '../testPart/TestPart'

// a11y: when the first item surface appears, focus the "Question N of N" heading; poll a few
// frames because it commits one render after loading flips, then fall back to the <main> landmark.
const READY_FOCUS_DELAY_MS = 150
const FIRST_ITEM_FOCUS_MAX_FRAMES = 5
const FIRST_ITEM_HEADING_SELECTOR = `[${TEST_ATTRIBUTE_ID}="${TEST_ELEMENT_IDS.numberOfQuestions}"]`
const QUESTION_AREA_SELECTOR = 'main'

export interface AssessmentComponentProps {
  userAssessment: UserAssessment
}

export const AssessmentComponent: FC<AssessmentComponentProps> = memo(({ userAssessment }) => {
  const { currentTest, itemsResponses, rubricBlock, isStarted } = useSnapshot(CurrentTestState)
  const { status, assessmentSettings, passedTime, addedTime } = useSnapshot(CurrentUserAssessmentState)
  const { title } = useSnapshot(HeaderTitleState)
  const { open: rubricModalOpened, onClose, onStart } = useRubricModal()
  const pauseUserAssessment = useUserAssessentPauseCallback()
  const handleTestCallback = useTestCallback()
  const answeredItems = useAnsweredItems()
  const loading = useUserAssessmentStart(userAssessment)
  const [searchParams] = useSearchParams()

  const skipDirections = searchParams.get('skipDirections') === 'true'
  const origin = searchParams.get('origin')
  const hasRubric = rubricBlock && rubricBlock.view === View.CANDIDATE

  const readyAnnouncedRef = useRef(false)

  // a11y: once the first item surface is presented — resume at boot, or after the start modal
  // closes — announce readiness and move focus to the first question (SC 4.1.3 + 2.4.3). While a
  // start/rubric modal is open (!isStarted) it focuses itself and the app-root announcer is
  // aria-hidden behind it, so this deliberately fires only on the no-modal branch. Fires once.
  useEffect(() => {
    if (readyAnnouncedRef.current || loading || !isStarted) return
    readyAnnouncedRef.current = true

    announce(`${getTerm('Test', true)} ready`)

    let isCancelled = false
    let remainingFrames = FIRST_ITEM_FOCUS_MAX_FRAMES
    const focusFirstItem = () => {
      if (isCancelled) return
      const questionHeading = document.querySelector<HTMLElement>(FIRST_ITEM_HEADING_SELECTOR)
      if (questionHeading) {
        questionHeading.focus()
        return
      }
      if (remainingFrames-- > 0) {
        requestAnimationFrame(focusFirstItem)
        return
      }
      document.querySelector<HTMLElement>(QUESTION_AREA_SELECTOR)?.focus()
    }
    // a11y: defer so "Test ready" is voiced before the focus move reads "Question N of N", and so
    // our focus wins over any modal/route focus restore that ran on the same transition.
    const focusTimer = window.setTimeout(() => requestAnimationFrame(focusFirstItem), READY_FOCUS_DELAY_MS)

    return () => {
      isCancelled = true
      window.clearTimeout(focusTimer)
    }
  }, [loading, isStarted])

  const getAdjustedDuration = () => {
    // Total time is base time plus any added time from accommodations
    const totalTime = assessmentSettings.timeLimits + (addedTime ?? 0)

    // Return remaining time after subtracting passed time
    // Ensure we don't return negative time
    return Math.max(0, totalTime - passedTime)
  }

  const onBack = async () => {
    await pauseUserAssessment()
    await handleTestCallback()
  }

  return (
    <>
      {/* Hide Loader from screen readers when modal is open */}
      {loading ? (
        <Loader aria-hidden={rubricModalOpened && !isStarted} />
      ) : (
        <TestPartComponent testPart={currentTest.testPart[0] as TestPart} />
      )}
      {currentTest && hasRubric && !skipDirections && !isStarted && (
        <RubricBlockModal
          open={rubricModalOpened}
          onClose={onClose}
          onStart={onStart}
          onCancel={onBack}
          title={title}
          content={rubricBlock.content}
          status={status}
          questionsAnswered={answeredItems.length}
          questionsTotal={itemsResponses.length}
          origin={origin}
        />
      )}
      {currentTest && (!hasRubric || skipDirections) && !isStarted && (
        <StartTestModal
          open={rubricModalOpened}
          onClose={onBack}
          onSubmit={onStart}
          title={title}
          itemsCount={itemsResponses.length}
          duration={getAdjustedDuration()}
          status={status}
          origin={origin}
        />
      )}
    </>
  )
})
